import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router";
import i18n from "@/i18n";

import { EXTERNAL_REGISTER_SETTINGS_ROUTES } from "../externalRegisterRoutes";

type ExternalRegisterAdminErrorBoundaryProps = {
  children: ReactNode;
  onReturnToLanding: () => void;
};

type ExternalRegisterAdminErrorBoundaryState = {
  hasError: boolean;
};

class ExternalRegisterAdminErrorBoundaryInner extends Component<
  ExternalRegisterAdminErrorBoundaryProps,
  ExternalRegisterAdminErrorBoundaryState
> {
  state: ExternalRegisterAdminErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): ExternalRegisterAdminErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV || import.meta.env.MODE === "test") {
      console.warn("[ExternalRegisterAdmin] Admin view crashed.", {
        componentStack: errorInfo.componentStack,
      });
    }
  }

  reset = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3 }}>
        <Card variant="outlined" sx={{ maxWidth: 840, borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {i18n.t("externalRegister.admin.errorBoundary.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {i18n.t("externalRegister.admin.errorBoundary.description")}
                </Typography>
              </Box>

              <Alert severity="error" variant="outlined">
                {i18n.t("externalRegister.admin.errorBoundary.alert")}
              </Alert>

              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={this.reset}>
                  {i18n.t("externalRegister.admin.errorBoundary.buttons.retry")}
                </Button>
                <Button variant="outlined" onClick={this.props.onReturnToLanding}>
                  {i18n.t("externalRegister.admin.errorBoundary.buttons.back")}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

export const ExternalRegisterAdminErrorBoundary = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  return (
    <ExternalRegisterAdminErrorBoundaryInner
      onReturnToLanding={() => navigate(EXTERNAL_REGISTER_SETTINGS_ROUTES.root)}
    >
      {children}
    </ExternalRegisterAdminErrorBoundaryInner>
  );
};
